import React, { useRef } from "react";
import type * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

export const HighchartsReactWithCallbackFix = ({
  options,
  highcharts,
}: {
  highcharts: typeof Highcharts;
  options: Highcharts.Options;
}) => {
  const supportedEvents = ["load"];
  // ensure only supported events have been configured
  if (
    !Object.keys(options.chart?.events || {}).every((x) =>
      supportedEvents.includes(x)
    )
  )
    throw new Error(
      "Highcharts configured with unsupported events for callback fix."
    );

  const callbackRef = useRef<Highcharts.ChartLoadCallbackFunction | undefined>(
    undefined
  );
  callbackRef.current = options?.chart?.events?.load;
  // Highcharts won't update callbacks without destroying the entire chart
  // https://github.com/highcharts/highcharts/issues/6538
  // so instead, we create a 'permanent' callback here that lives for the lifetime
  // of the chart
  // we can't call the callback directly as the scope is captured, so we use another ref
  // for that
  const persistentOnCompletedCallback = useRef<
    Highcharts.ChartLoadCallbackFunction
  >(function (args) {
    const currentCallback = callbackRef.current;
    currentCallback && currentCallback.call(this, args);
  });
  const modifiedOptions = {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...options?.chart?.events,
        load: persistentOnCompletedCallback.current,
      },
    },
  } as Highcharts.Options;
  // Highcharts has a few settings we need to tweak when in testing mode
  if (process.env.NODE_ENV === "test") {
    // boost mode causes exceptions running under jsdom
    // https://github.com/highcharts/highcharts/issues/13599
    if (modifiedOptions?.series) {
      modifiedOptions.series.forEach((x) => {
        (x as {
          boostThreshold: number | undefined;
        }).boostThreshold = undefined;
      });
    }
    // no yaxis label seems to cause highcharts to generate random widths
    // that break out snapshots, so just insert a placeholder when testing
    if (!(modifiedOptions?.yAxis as Highcharts.YAxisOptions)?.title?.text) {
      modifiedOptions.yAxis = {
        ...modifiedOptions?.yAxis,
        title: { text: "{{placeholder}}" },
      };
    }
    // so we don't get localized time differences in snapshots
    // switch to UTC even if we show localized to the user
    modifiedOptions.time = { useUTC: true };
  }

  // Handle Event only BS
  if (modifiedOptions.series?.length === 0) {
    if (modifiedOptions?.xAxis as Highcharts.XAxisOptions) {
      const xAxis = modifiedOptions?.xAxis as Highcharts.XAxisOptions;
      if (xAxis.plotLines !== undefined && xAxis.plotLines?.length > 0) {
        const min =
          xAxis.plotLines !== undefined ? xAxis.plotLines[0].value : null;
        const max =
          xAxis.plotLines !== undefined
            ? [...xAxis.plotLines].pop()?.value
            : null;
        modifiedOptions.xAxis = {
          ...modifiedOptions.xAxis,
          min: min,
          max: max,
        };
      }
      if (modifiedOptions?.series as Highcharts.SeriesOptionsType[]) {
        modifiedOptions.series = [
          {
            name: "Events",
            type: "line",
            color: "red",
            data: [0],
          },
        ];
      }
      modifiedOptions.title = {
        text: "Events",
        align: "center",
        verticalAlign: "top",
        margin: 20,
      };
    }
  }

  return <HighchartsReact highcharts={highcharts} options={modifiedOptions} />;
};
